import React from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { GalleryControls } from '@/libraries/design/components/gallery-controls/src/gallery-controls';
import { wacToken } from '@/libraries/design/foundations';
import {
	WCL_GRID_MAX_WIDTH,
	WCL_GRID_PADDING_LG,
	WCL_GRID_PADDING_SM,
} from '@/libraries/design/foundations/grid/src/base';

import { NavigationTabsLink } from './navigation-tabs-link';
import { useNavigationTabsScroll } from './use-navigation-tabs-scroll';

import type {
	NavigationTabDataType,
	NavigationTabsAppearance,
	NavigationTabsLayout,
} from './navigation-tabs-section';

const navigationCss = css({ width: '100%', maxWidth: '100%', minWidth: 0 });
const headerCss = css({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginBlockEnd: token('space.250'),
});
const eyebrowCss = css({
	marginBlock: 0,
	marginInlineEnd: 'auto',
	color: wacToken('color.primary.black'),
	fontFeatureSettings: "'liga' off, 'clig' off",
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	fontStyle: 'normal',
	fontWeight: 400,
	lineHeight: '18px',
	textAlign: 'left',
	textTransform: 'uppercase',
});
const gridMaxWidth = WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2;
const navigationTabsGridFrameCss = css({
	boxSizing: 'border-box',
	width: '100%',
	maxWidth: '100%',
	marginInline: 'auto',
	paddingInlineStart: `${WCL_GRID_PADDING_SM}px`,
	paddingInlineEnd: `${WCL_GRID_PADDING_SM}px`,
	[media.only.sm]: {
		paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
		paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
	},
	[media.above.md]: {
		paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
		paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
		maxWidth: `${gridMaxWidth}px`,
	},
});
const navigationTabsOverflowCss = css({
	boxSizing: 'border-box',
	width: '100%',
	maxWidth: '100%',
	paddingInlineStart: 'var(--navigation-tabs-grid-inset)',
	paddingInlineEnd: 0,
	scrollPaddingInlineStart: 'var(--navigation-tabs-grid-inset)',
	'--navigation-tabs-grid-inset': `${WCL_GRID_PADDING_SM}px`,
	[media.only.sm]: {
		'--navigation-tabs-grid-inset': `${WCL_GRID_PADDING_LG}px`,
	},
	[media.above.md]: {
		'--navigation-tabs-grid-inset': `max(${WCL_GRID_PADDING_LG}px, calc((100% - ${gridMaxWidth}px) / 2 + ${WCL_GRID_PADDING_LG}px))`,
	},
});
const controlsCss = css({ display: 'flex', marginInlineStart: 'auto' });
const viewportCss = css({
	width: '100%',
	maxWidth: '100%',
	minWidth: 0,
	overflowX: 'auto',
	['@supports (scrollbar-width: none)']: {
		scrollbarWidth: 'none',
	},
	[`&::-webkit-scrollbar`]: {
		display: 'none',
	},
});
const listCss = css({
	display: 'flex',
	listStyle: 'none',
	padding: 0,
	margin: 0,
	gap: token('space.100'),
});
// Stacked rows wrap in reading order with plain CSS. The browser packs each row
// as the viewport changes, so no measurement or re-render is needed.
const stackedListCss = css({ width: '100%', flexWrap: 'wrap' });
const stackedItemCss = css({ display: 'flex', minWidth: 0, maxWidth: '100%' });
const stackedViewportCss = css({ overflowX: 'visible' });
const wideStackedListCss = css({ justifyContent: 'center' });
const overflowListCss = css({ width: 'max-content', flexWrap: 'nowrap' });
const itemCss = css({ flexShrink: 0 });

export type NavigationTabsListProps = {
	tabsData: NavigationTabDataType[];
	appearance: NavigationTabsAppearance;
	layout: NavigationTabsLayout;
	eyebrow?: string;
	showControls: boolean;
	activeHref?: string;
	onLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>, href: string) => void;
};

export const NavigationTabsList = ({
	tabsData,
	appearance,
	layout,
	eyebrow,
	showControls,
	activeHref,
	onLinkClick,
}: NavigationTabsListProps) => {
	const intl = useIntl();
	const scroll = useNavigationTabsScroll();
	const controlDistance = scroll.viewportRef.current?.clientWidth ?? 300;
	return (
		<nav
			css={navigationCss}
			aria-label={intl.formatMessage({
				id: 'navigation-tabs.navigation-label',
				defaultMessage: 'Navigation tabs',
			})}
		>
			{(eyebrow || showControls) && (
				<div css={[headerCss, navigationTabsGridFrameCss]} data-testid="navigation-tabs-header">
					{eyebrow && (
						<p css={eyebrowCss} data-testid="navigation-tabs-eyebrow">
							{eyebrow}
						</p>
					)}
					{showControls && (
						<div css={controlsCss} data-testid="navigation-tabs-controls">
							<GalleryControls
								iconAppearance="chevron"
								onPrevious={() => scroll.scrollBy(-controlDistance)}
								onNext={() => scroll.scrollBy(controlDistance)}
								isFirstItemVisible={!scroll.scrollState.canScrollPrevious}
								isLastItemVisible={!scroll.scrollState.canScrollNext}
							/>
						</div>
					)}
				</div>
			)}
			<div
				ref={scroll.viewportRef}
				css={[
					viewportCss,
					layout === 'overflow' && navigationTabsOverflowCss,
					layout === 'stacked' && stackedViewportCss,
				]}
				data-testid="navigation-tabs-viewport"
				onPointerDown={scroll.onPointerDown}
				onPointerMove={scroll.onPointerMove}
				onPointerUp={scroll.onPointerEnd}
				onPointerCancel={scroll.onPointerCancel}
				onLostPointerCapture={scroll.onLostPointerCapture}
				onClickCapture={scroll.onClickCapture}
			>
				<ul
					ref={scroll.listRef}
					css={[
						listCss,
						layout === 'stacked' ? stackedListCss : overflowListCss,
						appearance === 'wide' && layout === 'stacked' && wideStackedListCss,
					]}
					data-layout={layout}
				>
					{tabsData.map((tab) => (
						<li key={tab.id} css={[itemCss, layout === 'stacked' && stackedItemCss]}>
							<NavigationTabsLink
								{...tab}
								appearance={appearance}
								isActive={activeHref === tab.href}
								onClick={onLinkClick ? (event) => onLinkClick(event, tab.href) : undefined}
							/>
						</li>
					))}
				</ul>
			</div>
		</nav>
	);
};
